import Decimal from "decimal.js";
import { isNumber } from "./";

const precision: number = 7;

/** Atalho para criar um objeto da classe DecimalAdapter.
 * OBS: Estou substituindo ele aos poucos pelas funções abaixo.
 */
export function decimal(
  value: number,
  decimalPrecision?: number
): DecimalAdapter {
  return new DecimalAdapter(value, decimalPrecision);
}

/** Soma todos os valores informados. */
export function sum(...values: number[]) {
  return decimal(values[0])
    .sum(...values.slice(1))
    .toNumber();
}

/** Subtrai todos os valores informados. */
export function subtract(...values: number[]) {
  return decimal(values[0])
    .subtract(...values.slice(1))
    .toNumber();
}

/** Multiplica o valueA pelo valueB. */
export function times(valueA: number, valueB: number) {
  return decimal(valueA).times(valueB).toNumber();
}

/** Divide o valueA pelo valueB. */
export function dividedBy(valueA: number, valueB: number) {
  return decimal(valueA).dividedBy(valueB).toNumber();
}

/** Obtém o percentual de um valor sobre o total. O percentual devolvido
 * é multiplicado por 100.
 */
export function valueToPercentual(value: number, total: number): number {
  if (decimal(total, 2).isEqualTo(0)) return 0;

  const dec = new DecimalAdapter(value, 2);
  if (dec.isEqualTo(total)) return 100;

  return dec.dividedBy(total).times(100).toNumber();
}

/** Obtém o valor de uma porcentagem do total. É considerado que
 * o percentual é multiplicado por 100.
 */
export function percentualToValue(
  percentual: number,
  total: number,
  decimalPrecision?: number
) {
  return decimal(percentual, decimalPrecision)
    .dividedBy(100)
    .times(total)
    .round();
}

/** Devolve se valueA é maior que valueB considerando as
 * casas decimais informadas.
 */
export function isGreaterThan(
  valueA: number,
  valueB: number,
  decimalPrecision?: number
): boolean {
  const decimalA = toDecimal(valueA, decimalPrecision);
  const decimalB = toDecimal(valueB, decimalPrecision);

  return decimalA.greaterThan(decimalB);
}

/** Criar uma instância de Decimal já com a quantidade de casas
 * decimais definida.
 */
function toDecimal(value, decimalPrecision: number = precision): Decimal {
  return new Decimal(toNumber(value)).toDecimalPlaces(decimalPrecision);
}

/** Transforma em número, caso o valor seja inválido retorna 0 (zero). */
function toNumber(value: number): number {
  value = Number(value);
  return isNumber(value) ? value : 0;
}

/** Classe que aplica adaptações na biblioteca decimal.js para
 * realização de cálculos financeiros no sistema.
 */
class DecimalAdapter {
  private valueA: Decimal;
  private decimalPlaces: number;

  constructor(value: number | Decimal, decimalPrecision: number = precision) {
    decimalPrecision = toNumber(decimalPrecision);
    this.decimalPlaces = decimalPrecision;

    if (value instanceof Decimal) this.valueA = value;
    else this.valueA = this.convertToDecimal(value);
  }

  private get valueARounded(): Decimal {
    return this.valueA.toDecimalPlaces(this.decimalPlaces);
  }

  /* OPERACOES */
  sum(...values: number[]): DecimalAdapter {
    const result = values.reduce(
      (total: Decimal, valueB: number) =>
        total.plus(toNumber(valueB)).toDecimalPlaces(precision),
      this.valueA
    );

    return this.createObject(result);
  }

  subtract(...values: number[]): DecimalAdapter {
    const result = values.reduce(
      (total: Decimal, valueB: number) =>
        total.minus(toNumber(valueB)).toDecimalPlaces(precision),
      this.valueA
    );

    return this.createObject(result);
  }

  times(value: number): DecimalAdapter {
    const result = this.valueA.times(toNumber(value));
    return this.createObject(result);
  }

  dividedBy(value: number): DecimalAdapter {
    value = toNumber(value);
    const result = !value ? 0 : this.valueA.div(value);
    return this.createObject(result);
  }

  /* ARREDONDAMENTO */
  toNumber(): number {
    return this.valueA.toDecimalPlaces(precision).toNumber();
  }

  round(): number {
    return this.valueARounded.toNumber();
  }

  /* COMPARACOES */
  isGreaterThan(value: number): boolean {
    const valueB = this.convertToDecimal(value).toDecimalPlaces(
      this.decimalPlaces
    );
    return this.valueARounded.greaterThan(valueB);
  }

  isLessThan(value: number): boolean {
    const valueB = this.convertToDecimal(value).toDecimalPlaces(
      this.decimalPlaces
    );
    return this.valueARounded.lessThan(valueB);
  }

  isEqualTo(value: number): boolean {
    const valueB = this.convertToDecimal(value).toDecimalPlaces(
      this.decimalPlaces
    );
    return this.valueARounded.equals(valueB);
  }

  /* Técnica utilizada no ADO */
  setToMaxOrMin(maxValueToCompare: number, minValueToCompare: number): number {
    if (this.isEqualTo(minValueToCompare))
      this.valueA = this.convertToDecimal(minValueToCompare);
    else if (this.isEqualTo(maxValueToCompare))
      this.valueA = this.convertToDecimal(maxValueToCompare);

    return this.toNumber();
  }

  private convertToDecimal(value: number): Decimal {
    return new Decimal(toNumber(value)).toDecimalPlaces(precision);
  }

  private createObject(value: number | Decimal) {
    return new DecimalAdapter(value, this.decimalPlaces);
  }
}

export default DecimalAdapter;
